// src/services/atribuicaoVisitante.js -- first-touch de afiliado, RTDB ou API.
//
// Plano §2.1: `/visitorId/<hash>/queryString` (75.114 nos) vira
// `rtdb_atribuicao_visitante` + 2 rotas. Leitura no boot do App.vue e escrita
// quando a URL traz querystring (App.vue:390,415-423 antes da F2).
//
// Interface das tres implementacoes:
//   ler(visitorId) -> queryString | null
//   gravar(visitorId, queryString) -> void
import { carregarPecasDoRtdb } from './rtdbSobDemanda.js';
import { registrarDivergencia, tolerarFalhaDaApi } from './sombra.js';

export const MODOS_DE_ATRIBUICAO = { RTDB: 'rtdb', SOMBRA: 'sombra', API: 'api' };

const ROTA_ATRIBUICAO = '/dashboard/api/atribuicao/';
const NAO_ENCONTRADO = 404;

export class AtribuicaoRtdb {
  /** @param {{lerNo: Function, gravarNo: Function}} rtdb  cliente de rtdbCliente.js */
  constructor(rtdb) {
    this.rtdb = rtdb;
  }

  async ler(visitorId) {
    return this.rtdb.lerNo(caminhoDoVisitante(visitorId));
  }

  // `set()` sobrescreve: o RTDB e' LAST-touch quando o visitante volta com
  // outra querystring. A API faz O MESMO -- apesar do nome
  // `registrar_first_touch`, ela usa `update_or_create(defaults=...)` e
  // sobrescreve de proposito, para a F3 trocar a FONTE sem trocar a regra de
  // negocio (dashboard/atribuicao_visitante.py:35-47). Conferido em
  // 12/09/2026: 40 de 40 valores identicos nos dois lados.
  async gravar(visitorId, queryString) {
    await this.rtdb.gravarNo(caminhoDoVisitante(visitorId), queryString);
  }
}

function caminhoDoVisitante(visitorId) {
  return `visitorId/${visitorId}/queryString`;
}

export class AtribuicaoApi {
  /** @param {{get: Function, post: Function}} http  instancia axios de services/api.js */
  constructor(http) {
    this.http = http;
  }

  async ler(visitorId) {
    try {
      const resposta = await this.http.get(
        `${ROTA_ATRIBUICAO}${encodeURIComponent(visitorId)}/`);
      return resposta.data.query_string || null;
    } catch (erro) {
      // 404 e' a resposta normal de visitante novo (views_vitalicio.py:73-75),
      // nao erro: o chamador so quer saber se ha querystring guardada.
      if (erro.response && erro.response.status === NAO_ENCONTRADO) {
        return null;
      }
      throw erro;
    }
  }

  async gravar(visitorId, queryString) {
    await this.http.post(ROTA_ATRIBUICAO,
      { visitor_id: visitorId, query_string: queryString });
  }
}

export class AtribuicaoSombra {
  constructor(atribuicaoRtdb, atribuicaoApi, registrar = registrarDivergencia) {
    this.atribuicaoRtdb = atribuicaoRtdb;
    this.atribuicaoApi = atribuicaoApi;
    this.registrar = registrar;
  }

  /** Chama as duas, DECIDE pela RTDB e loga a divergencia. Erro da API nao vaza. */
  async ler(visitorId) {
    const [daRtdb, daApi] = await Promise.all([
      this.atribuicaoRtdb.ler(visitorId),
      tolerarFalhaDaApi(() => this.atribuicaoApi.ler(visitorId)),
    ]);
    if (daRtdb !== daApi) {
      this.registrar('atribuicao_sombra_divergencia',
        { visitor_id: visitorId, rtdb: daRtdb, api: daApi });
    }
    return daRtdb;
  }

  async gravar(visitorId, queryString) {
    await Promise.all([
      this.atribuicaoRtdb.gravar(visitorId, queryString),
      tolerarFalhaDaApi(() => this.atribuicaoApi.gravar(visitorId, queryString)),
    ]);
  }
}

const atribuicoesPorModo = new Map();

/**
 * Atribuicao do modo de build. Default `rtdb`.
 *
 * Exemplo:
 *   const atribuicao = await criarAtribuicao();
 *   const queryString = await atribuicao.ler(visitorId);
 */
export async function criarAtribuicao(
  modo = process.env.VUE_APP_ATRIBUICAO || MODOS_DE_ATRIBUICAO.RTDB) {
  if (!atribuicoesPorModo.has(modo)) {
    atribuicoesPorModo.set(modo, await montarAtribuicao(modo));
  }
  return atribuicoesPorModo.get(modo);
}

async function montarAtribuicao(modo) {
  if (!Object.values(MODOS_DE_ATRIBUICAO).includes(modo)) {
    throw new Error(`VUE_APP_ATRIBUICAO=${modo} desconhecido. Esperado: rtdb|sombra|api`);
  }
  const { default: http } = await import('./api.js');
  if (modo === MODOS_DE_ATRIBUICAO.API) {
    return new AtribuicaoApi(http);
  }
  const { rtdb } = await carregarPecasDoRtdb();
  const atribuicaoRtdb = new AtribuicaoRtdb(rtdb);
  return modo === MODOS_DE_ATRIBUICAO.RTDB
    ? atribuicaoRtdb
    : new AtribuicaoSombra(atribuicaoRtdb, new AtribuicaoApi(http));
}
